// ONE IDEMPOTENCY-KEY PER INTENT for a form that posts a write (the D-308
// review of 04-A-2; the end-stoppage dialog's rule). Its own module, beside
// production-queries.ts rather than in it, because the screen tests mock that
// module whole and this is logic the forms run, not a query to stub.

import { useMemo, useRef } from "react";
import { newIdempotencyKey, RequestError } from "../../../../lib/request";

/**
 * Whether a failed write may still have committed, so a retry must carry the
 * same key (the D-308 review). A network failure or a 5xx leaves the outcome
 * unknown: a gateway can drop the response of a request the API committed,
 * and the server releases the key of a request it failed itself, so the same
 * key either replays the stored answer or runs cleanly. A 4xx is a settled
 * refusal: nothing committed and the server stored that refusal against the
 * key, so the same key would replay it after its cause has gone (a held drum
 * released, a drum drained a moment ago now answering as bulk). 409
 * `IDEMPOTENCY_IN_PROGRESS` is the exception: the first request is still
 * running, and its answer belongs to the same key.
 */
export function outcomeUnknown(error: unknown): boolean {
  if (!(error instanceof RequestError)) return true;
  if (error.httpStatus >= 500) return true;
  return error.code === "IDEMPOTENCY_IN_PROGRESS";
}

/**
 * One `Idempotency-Key` per INTENT, for a form that posts a write (the D-308
 * review; the end-stoppage dialog's rule). `keyFor(fingerprint)` returns the
 * key of the attempt in hand while the body is unchanged and mints a new one
 * when the body changes. `settle(error?)` ends the intent after a success or a
 * settled refusal and keeps it while the outcome is unknown
 * ({@link outcomeUnknown}), so pressing the same button again replays rather
 * than posting twice.
 */
export function useIntentKey(): {
  keyFor: (fingerprint: string) => string;
  settle: (error?: unknown) => void;
} {
  const intent = useRef<{ fingerprint: string; key: string } | undefined>(undefined);
  return useMemo(
    () => ({
      keyFor: (fingerprint: string): string => {
        const current = intent.current;
        if (current?.fingerprint === fingerprint) return current.key;
        const key = newIdempotencyKey();
        intent.current = { fingerprint, key };
        return key;
      },
      settle: (error?: unknown): void => {
        if (error === undefined || !outcomeUnknown(error)) intent.current = undefined;
      },
    }),
    [],
  );
}
